import React from 'react';
import { times } from 'lodash';
import { IconAddCircle, IconDelete } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { Button } from '@bynder/design-system';
import assertNever from '../../helpers/assertNever';
import TableIndexHelper, { selectIndex } from '../../helpers/TableIndexHelper';
import useOutsideClick from '../../hooks/useOutsideClick';
import {
  AddButton,
  Cell as StyledCell,
  ColumnControl,
  ColumnControlMenu,
  DIRECTION,
  RowControl,
  RowControlMenu,
  Table,
  TableContainer,
  TableControl,
} from '../shared/styled-components/TableWidget';
import Tooltip from '../shared/Tooltip';
import { MAX_COLUMNS, MAX_ROWS } from '../../constants/widgets';

type Row = {
  cells: Cell[];
};

type Cell = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: any;
};

interface Props {
  rows?: Row[];
  isEditable?: boolean;
  onAddRow: (index: number) => void;
  onAddColumn: (index: number) => void;
  onRemoveColumn: (index: number) => void;
  onRemoveRow: (index: number) => void;
  children: (
    rowIndex: number,
    cellIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content?: any,
  ) => React.ReactElement;
}

const TableWidgetSkeleton: React.FC<Props> = ({
  rows,
  isEditable,
  onAddRow,
  onAddColumn,
  onRemoveColumn,
  onRemoveRow,
  children,
}) => {
  const { translate } = useTranslate();

  const [isActive, setIsActive] = React.useState(false);
  const [highlightedAddCol, setHighlightedAddCol] = React.useState<
    number | null
  >(null);
  const [highlightedAddRow, setHighlightedAddRow] = React.useState<
    number | null
  >(null);
  const [highlightedCol, setHighlightedCol] = React.useState<number | null>(
    null,
  );
  const [highlightedRow, setHighlightedRow] = React.useState<number | null>(
    null,
  );
  const [selectedCol, setSelectedCol] = React.useState<number | null>(null);
  const [selectedRow, setSelectedRow] = React.useState<number | null>(null);
  const [hoveredDeleteButton, setHoveredDeleteButton] = React.useState(false);
  const tableRef = useOutsideClick<HTMLTableElement>(() => {
    clearStates();
    setIsActive(false);
  });

  const columnCount = rows[0]?.cells?.length;
  const alignMaxColumnTooltip = { offset: [0, -33] };
  const alignMaxRowTooltip = { offset: [0, -5] };

  const clearStates = () => {
    setHighlightedAddCol(null);
    setHighlightedAddRow(null);
    setHighlightedCol(null);
    setHighlightedRow(null);
    setSelectedCol(null);
    setSelectedRow(null);
    setHoveredDeleteButton(null);
  };

  const onClickControl = (index: number, controlType: 'column' | 'row') => {
    switch (controlType) {
      case 'column':
        setSelectedRow(null);
        setSelectedCol(state => selectIndex(state, index));
        break;
      case 'row':
        setSelectedCol(null);
        setSelectedRow(state => selectIndex(state, index));
        break;
      default:
        assertNever(controlType);
    }
  };

  const onClickRemoveColumn = (index: number) => {
    clearStates();
    onRemoveColumn(index);
  };

  const onClickRemoveRow = (index: number) => {
    clearStates();
    onRemoveRow(index);
  };

  const onClickAddColumn = (index: number) => {
    clearStates();
    onAddColumn(index);
  };

  const onClickAddRow = (index: number) => {
    clearStates();
    onAddRow(index);
  };

  return (
    <TableContainer data-testid="tableWidget">
      <Table $showAsEditable={isEditable && isActive} ref={tableRef}>
        <tbody>
          {isEditable && isActive && (
            <tr>
              <TableControl
                $highlightedAddRow={TableIndexHelper.isFirst(highlightedAddRow)}
                $highlightedAddCol={TableIndexHelper.isFirst(highlightedAddCol)}
              >
                <Tooltip
                  hidden={columnCount < MAX_COLUMNS}
                  label={translate(
                    'TABLE_WIDGET_SKELETON.CONTROL_TOOLTIP_LABEL_COLUMNS',
                  )}
                  hideOnLeave
                  align={alignMaxColumnTooltip}
                >
                  <span>
                    <AddButton
                      onClick={() => onClickAddColumn(0)}
                      onMouseEnter={() => setHighlightedAddCol(0)}
                      onMouseLeave={() => setHighlightedAddCol(null)}
                      disabled={columnCount >= MAX_COLUMNS}
                    >
                      <IconAddCircle />
                    </AddButton>
                  </span>
                </Tooltip>

                <Tooltip
                  hidden={rows.length < MAX_ROWS}
                  label={translate(
                    'TABLE_WIDGET_SKELETON.CONTROL_TOOLTIP_LABEL_ROW',
                  )}
                  hideOnLeave
                  align={alignMaxRowTooltip}
                >
                  <span>
                    <AddButton
                      direction={DIRECTION.ROW}
                      onClick={() => onClickAddRow(0)}
                      onMouseEnter={() => setHighlightedAddRow(0)}
                      onMouseLeave={() => setHighlightedAddRow(null)}
                      disabled={rows.length >= MAX_ROWS}
                    >
                      <IconAddCircle />
                    </AddButton>
                  </span>
                </Tooltip>
              </TableControl>

              {times(columnCount, index => {
                const checkColumn = new TableIndexHelper(index);

                return (
                  <TableControl
                    key={index}
                    direction={DIRECTION.COLUMN}
                    $highlightedAddRow={TableIndexHelper.isFirst(
                      highlightedAddRow,
                    )}
                    $highlightedAddCol={checkColumn.isCurrent(
                      highlightedAddCol,
                    )}
                  >
                    {checkColumn.isCurrent(selectedCol) && (
                      <ColumnControlMenu>
                        <Tooltip
                          label={
                            columnCount <= 1
                              ? translate(
                                  'TABLE_WIDGET_SKELETON.DELETE_COLUMN_DISABLED',
                                )
                              : translate(
                                  'TABLE_WIDGET_SKELETON.DELETE_COLUMN_ENABLED',
                                )
                          }
                          hideOnLeave
                        >
                          <span>
                            <Button
                              aria-label="delete column"
                              variant="clean"
                              icon={<IconDelete />}
                              isSmall
                              onClick={() => onClickRemoveColumn(index)}
                              isDisabled={columnCount <= 1}
                            />
                          </span>
                        </Tooltip>
                      </ColumnControlMenu>
                    )}
                    <ColumnControl
                      selectedCol={checkColumn.isCurrent(selectedCol)}
                      onMouseEnter={() => setHighlightedCol(index + 1)}
                      onMouseLeave={() => setHighlightedCol(null)}
                      onClick={() => onClickControl(index, 'column')}
                      hoveredDeleteButton={hoveredDeleteButton}
                    />
                    <Tooltip
                      hidden={columnCount < MAX_COLUMNS}
                      label={translate(
                        'TABLE_WIDGET_SKELETON.ADD_COLUMN_DISABLED',
                      )}
                      hideOnLeave
                      align={alignMaxColumnTooltip}
                    >
                      <span>
                        <AddButton
                          direction={DIRECTION.COLUMN}
                          onClick={() => onClickAddColumn(index + 1)}
                          onMouseEnter={() => setHighlightedAddCol(index + 1)}
                          onMouseLeave={() => setHighlightedAddCol(null)}
                          disabled={columnCount >= MAX_COLUMNS}
                        >
                          <IconAddCircle />
                        </AddButton>
                      </span>
                    </Tooltip>
                  </TableControl>
                );
              })}
            </tr>
          )}

          {rows.map((row, rowIndex) => {
            const checkRow = new TableIndexHelper(rowIndex);

            return (
              <tr key={rowIndex}>
                {isEditable && isActive && (
                  <TableControl
                    key={rowIndex}
                    direction={DIRECTION.ROW}
                    $highlightedAddCol={TableIndexHelper.isFirst(
                      highlightedAddCol,
                    )}
                    $highlightedAddRow={checkRow.isCurrent(highlightedAddRow)}
                  >
                    {checkRow.isCurrent(selectedRow) && (
                      <RowControlMenu>
                        <Tooltip
                          label={
                            rows.length <= 1
                              ? translate(
                                  'TABLE_WIDGET_SKELETON.DELETE_ROW_DISABLED',
                                )
                              : translate(
                                  'TABLE_WIDGET_SKELETON.DELETE_ROW_ENABLED',
                                )
                          }
                          hideOnLeave
                        >
                          <span>
                            <Button
                              aria-label="delete row"
                              variant="clean"
                              icon={<IconDelete />}
                              isSmall
                              onClick={() => onClickRemoveRow(rowIndex)}
                              isDisabled={rows.length <= 1}
                            />
                          </span>
                        </Tooltip>
                      </RowControlMenu>
                    )}
                    <RowControl
                      highlightedAddCol={TableIndexHelper.isFirst(
                        highlightedAddCol,
                      )}
                      selectedRow={checkRow.isCurrent(selectedRow)}
                      onMouseEnter={() => setHighlightedRow(rowIndex + 1)}
                      onMouseLeave={() => setHighlightedRow(null)}
                      onClick={() => onClickControl(rowIndex, 'row')}
                      hoveredDeleteButton={hoveredDeleteButton}
                    />
                    <Tooltip
                      hidden={rows.length < MAX_ROWS}
                      label={translate(
                        'TABLE_WIDGET_SKELETON.ADD_ROW_DISABLED',
                      )}
                      hideOnLeave
                      align={alignMaxRowTooltip}
                    >
                      <span>
                        <AddButton
                          direction={DIRECTION.ROW}
                          onClick={() => onClickAddRow(rowIndex + 1)}
                          onMouseEnter={() =>
                            setHighlightedAddRow(rowIndex + 1)
                          }
                          onMouseLeave={() => setHighlightedAddRow(null)}
                          disabled={rows.length >= MAX_ROWS}
                        >
                          <IconAddCircle />
                        </AddButton>
                      </span>
                    </Tooltip>
                  </TableControl>
                )}
                {row.cells.map((cell, cellIndex) => {
                  const checkColumn = new TableIndexHelper(cellIndex);

                  return (
                    <StyledCell
                      key={cellIndex}
                      $highlightedAddRow={checkRow.isCurrent(highlightedAddRow)}
                      $highlightedAddCol={checkColumn.isCurrent(
                        highlightedAddCol,
                      )}
                      $highlightedCol={checkColumn.isCurrent(highlightedCol)}
                      $highlightedRow={checkRow.isCurrent(highlightedRow)}
                      $selectedCol={checkColumn.isCurrent(selectedCol)}
                      $selectedRow={checkRow.isCurrent(selectedRow)}
                      $hoveredDeleteButton={hoveredDeleteButton}
                      onClick={() => setIsActive(true)}
                      $showAsEditable={isEditable && isActive}
                      $lastRow={rows.length === rowIndex + 1}
                      $lastColumn={row.cells.length === cellIndex + 1}
                    >
                      {children(rowIndex, cellIndex, cell.content)}
                    </StyledCell>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default TableWidgetSkeleton;
